import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Sparkles, Loader2, Calendar, ChevronLeft, ChevronRight,
  Play, RefreshCw, Check, X,
} from "lucide-react";

const DAY_LABELS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
// 9:00 → 18:00, 30-min rows for fine drop precision (18 rows = 9 hrs)
const START_HOUR = 9;
const END_HOUR = 18;
const SLOTS = (END_HOUR - START_HOUR) * 2;
const SLOT_PX = 26;

function getMonday(d) {
  const x = new Date(d);
  const wd = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - wd);
  x.setHours(0, 0, 0, 0);
  return x;
}
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fmtSlot(idx) {
  const total = START_HOUR * 60 + idx * 30;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
function timeToSlot(hhmm) {
  if (!hhmm || !/^\d{1,2}:\d{2}/.test(hhmm)) return null;
  const [h, m] = hhmm.split(":").map(Number);
  const offset = (h - START_HOUR) * 2 + Math.floor(m / 30);
  if (offset < 0 || offset >= SLOTS) return null;
  return offset;
}

export default function WeekBoard() {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(() => getMonday(new Date()));
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [proposal, setProposal] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [err, setErr] = useState(null);
  const [drag, setDrag] = useState(null);

  const weekStart = useMemo(() => ymd(anchor), [anchor]);
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => {
      const d = new Date(anchor);
      d.setDate(d.getDate() + i);
      return d;
    }),
    [anchor]
  );

  const load = async () => {
    try {
      const [t, ev] = await Promise.all([
        api.get(`/weekly-tasks?week_start=${weekStart}`).then((r) => r.data),
        api.get("/calendar-events").then((r) => r.data),
      ]);
      setTasks(Array.isArray(t) ? t : []);
      setEvents(Array.isArray(ev) ? ev : []);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => { load(); }, [weekStart]);

  const scheduled = tasks.filter((t) => t.scheduled_date && t.scheduled_start);
  const unscheduled = tasks.filter((t) => !t.scheduled_date && !t.done && (t.title || "").trim());

  const askAI = async () => {
    setPlanning(true);
    setErr(null);
    try {
      const r = await api.post("/ai/schedule");
      if (!r.data?.blocks || r.data.blocks.length === 0) {
        setErr(r.data?.note || "Nothing to schedule.");
      } else {
        setProposal(r.data.blocks);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "AI scheduling failed");
    } finally {
      setPlanning(false);
    }
  };

  const acceptProposal = async () => {
    if (!proposal) return;
    setPlanning(true);
    try {
      await api.post("/ai/schedule/apply", { blocks: proposal });
      setProposal(null);
      await load();
    } catch (e) {
      setErr("Could not apply schedule");
    } finally {
      setPlanning(false);
    }
  };

  const onDragStart = (task) => (e) => {
    setDrag(task);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  const dropOnSlot = (day, slotIdx) => async (e) => {
    e.preventDefault();
    if (!drag) return;
    const start = fmtSlot(slotIdx);
    const end = fmtSlot(Math.min(slotIdx + 1, SLOTS - 1));
    const updated = {
      week_start: drag.week_start,
      day: drag.day,
      title: drag.title,
      done: drag.done,
      order: drag.order,
      scheduled_date: ymd(day),
      scheduled_start: start,
      scheduled_end: end,
      due_date: drag.due_date || null,
    };
    setDrag(null);
    try {
      await api.put(`/weekly-tasks/${drag.id}`, updated);
      await load();
    } catch (e) { /* swallow */ }
  };

  const unschedule = async (t) => {
    await api.put(`/weekly-tasks/${t.id}`, {
      week_start: t.week_start, day: t.day, title: t.title,
      done: t.done, order: t.order,
      scheduled_date: null, scheduled_start: null, scheduled_end: null,
    });
    await load();
  };

  const taskTitle = (id) => tasks.find((t) => t.id === id)?.title || "(task)";

  return (
    <div className="space-y-6" data-testid="week-board">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="kpi-label">Time-block board</div>
          <h1 className="display text-3xl sm:text-4xl mt-2 leading-none font-bold uppercase tracking-tight">
            Plan the<span className="text-oud">_</span>week.
          </h1>
          <p className="mono text-xs text-muted-foreground tracking-widest mt-2 uppercase">
            Drag tasks into the grid · or let AI slot everything for you
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const d = new Date(anchor); d.setDate(d.getDate() - 7); setAnchor(d);
            }}
            className="hud-btn !p-2"
            data-testid="board-prev-week"
            aria-label="Previous week"
          >
            <ChevronLeft size={14} />
          </button>
          <div className="mono text-[11px] uppercase tracking-widest min-w-[120px] text-center">
            Wk of {weekStart}
          </div>
          <button
            onClick={() => {
              const d = new Date(anchor); d.setDate(d.getDate() + 7); setAnchor(d);
            }}
            className="hud-btn !p-2"
            data-testid="board-next-week"
            aria-label="Next week"
          >
            <ChevronRight size={14} />
          </button>
          <button
            onClick={askAI}
            disabled={planning}
            className="hud-btn hud-btn-primary flex items-center gap-2"
            data-testid="board-ai-plan"
          >
            {planning ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {planning ? "PLANNING…" : "AI PLAN MY WEEK"}
          </button>
        </div>
      </header>

      {err && (
        <div className="hud-frame p-3 mono text-xs text-[hsl(var(--oxblood))]" data-testid="board-error">
          {err}
        </div>
      )}

      {proposal && (
        <div className="hud-frame p-5" data-testid="board-proposal">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="kpi-label flex items-center gap-2">
                <Sparkles size={11} className="text-oud" /> AI proposal · {proposal.length} blocks
              </div>
              <div className="display text-lg font-bold uppercase tracking-tight mt-1">
                Review &amp; apply
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setProposal(null)} className="hud-btn !text-[10px]" data-testid="board-reject">
                <X size={11} /> DISCARD
              </button>
              <button onClick={acceptProposal} className="hud-btn hud-btn-primary !text-[10px]" data-testid="board-accept">
                <Check size={11} /> APPLY ALL
              </button>
            </div>
          </div>
          <ul className="space-y-1.5">
            {proposal.map((b, i) => (
              <li key={i} className="flex items-baseline gap-3 text-sm">
                <span className="mono text-[10px] tracking-widest text-muted-foreground w-32">
                  {b.date} · {b.start_time}–{b.end_time}
                </span>
                <span className="font-medium flex-1 min-w-0 truncate">{taskTitle(b.task_id)}</span>
                <span className="text-xs text-muted-foreground italic truncate max-w-md">
                  {b.rationale}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        {/* Unscheduled tray */}
        <aside className="hud-frame p-4" data-testid="board-unscheduled">
          <div className="kpi-label mb-3">Unscheduled · {unscheduled.length}</div>
          {unscheduled.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">
              Nothing pending. Add tasks in Weekly OS or let AI propose.
            </div>
          ) : (
            <ul className="space-y-2">
              {unscheduled.map((t) => (
                <li
                  key={t.id}
                  draggable
                  onDragStart={onDragStart(t)}
                  onDragEnd={() => setDrag(null)}
                  className="border border-border bg-[hsl(var(--surface))] px-3 py-2 text-sm cursor-grab hover:border-oud transition-colors"
                  data-testid={`unscheduled-task-${t.id}`}
                >
                  <div className="font-medium truncate">{t.title}</div>
                  {t.due_date && (
                    <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground mt-1">
                      Due {t.due_date}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Week grid */}
        <div className="hud-frame p-2 overflow-x-auto">
          <div className="grid grid-cols-[60px_repeat(7,1fr)] min-w-[820px]">
            <div />
            {days.map((d, i) => (
              <div key={i} className="text-center pb-2 pt-1">
                <div className="kpi-label">{DAY_LABELS[i]}</div>
                <div className="mono text-[10px] mt-0.5">
                  {String(d.getMonth() + 1).padStart(2, "0")}/{String(d.getDate()).padStart(2, "0")}
                </div>
              </div>
            ))}

            {/* Time column */}
            <div>
              {Array.from({ length: SLOTS }).map((_, i) => (
                i % 2 === 0 ? (
                  <div
                    key={i}
                    style={{ height: SLOT_PX }}
                    className="mono text-[9px] text-muted-foreground tracking-widest text-right pr-2 -mt-1.5"
                  >
                    {fmtSlot(i)}
                  </div>
                ) : (
                  <div key={i} style={{ height: SLOT_PX }} />
                )
              ))}
            </div>

            {/* Day columns */}
            {days.map((day, di) => {
              const dayStr = ymd(day);
              const dayEvents = events.filter((e) => e.date === dayStr);
              const dayTasks = scheduled.filter((t) => t.scheduled_date === dayStr);
              return (
                <div
                  key={di}
                  className="relative border-l border-border/70"
                  style={{ height: SLOTS * SLOT_PX }}
                  data-testid={`board-day-${di}`}
                >
                  {/* Grid slot underlays */}
                  {Array.from({ length: SLOTS }).map((_, si) => (
                    <div
                      key={si}
                      onDragOver={onDragOver}
                      onDrop={dropOnSlot(day, si)}
                      style={{ top: si * SLOT_PX, height: SLOT_PX }}
                      className={`absolute inset-x-0 ${si % 2 === 0 ? "border-t border-border/40" : ""}`}
                    />
                  ))}
                  {/* Events */}
                  {dayEvents.map((e) => {
                    const s = timeToSlot(e.start_time);
                    const en = timeToSlot(e.end_time) ?? (s != null ? s + 2 : null);
                    if (s == null || en == null) return null;
                    const top = s * SLOT_PX;
                    const h = Math.max(SLOT_PX, (en - s) * SLOT_PX);
                    return (
                      <div
                        key={e.id}
                        className="absolute inset-x-1 px-2 py-1 text-[11px] leading-tight border-l-2 border-oud bg-[hsl(var(--oud)/0.18)] backdrop-blur-sm pointer-events-none"
                        style={{ top, height: h }}
                      >
                        <div className="font-medium truncate">{e.title || "Event"}</div>
                        <div className="mono text-[9px] tracking-widest text-muted-foreground">
                          {e.start_time}–{e.end_time || ""}
                        </div>
                      </div>
                    );
                  })}
                  {/* Scheduled tasks */}
                  {dayTasks.map((t) => {
                    const s = timeToSlot(t.scheduled_start);
                    const en = timeToSlot(t.scheduled_end) ?? (s != null ? s + 1 : null);
                    if (s == null || en == null) return null;
                    const top = s * SLOT_PX;
                    const h = Math.max(SLOT_PX, (en - s) * SLOT_PX);
                    return (
                      <div
                        key={t.id}
                        className="absolute inset-x-1 px-2 py-1 text-[11px] leading-tight border border-oxblood bg-[hsl(var(--oxblood)/0.18)] backdrop-blur-sm group flex items-start gap-1 cursor-pointer hover:bg-[hsl(var(--oxblood)/0.28)] transition-colors"
                        style={{ top, height: h }}
                        data-testid={`scheduled-${t.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{t.title}</div>
                          <div className="mono text-[9px] tracking-widest text-muted-foreground">
                            {t.scheduled_start}–{t.scheduled_end}
                          </div>
                        </div>
                        <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => navigate(`/focus/${t.id}`)}
                            className="p-0.5 hover:bg-[hsl(var(--oxblood))] hover:text-white"
                            title="Focus mode"
                            data-testid={`focus-${t.id}`}
                          >
                            <Play size={9} />
                          </button>
                          <button
                            onClick={() => unschedule(t)}
                            className="p-0.5 hover:bg-[hsl(var(--oxblood))] hover:text-white"
                            title="Unschedule"
                          >
                            <X size={9} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
